import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../shared-functions/ConvertUTCtoHHMM';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import transaction from '../../utils/api/transaction';

interface TransactionSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface TransactionDataProperties {
  _id: string;
  chargingPointSn: string;
  connector: number;
  status: string;
  dynamicPricing: {
    pricingType: string;
    price: string;
  };
  initialChargePercentage: string;
  desiredChargePercentage: string;
  batteryCapacity: string;
  chargingStation: any[]; // if you have its shape, you can define it better
  stripeCustomerObjectId: string;
  authorizePaymentIntentObjectId: string;
  paymentIntentObjectId: string;
  userCustomId: string;
  accountCustomId: string;
  isGuestUser: boolean;
  createdAt: string;
  updatedAt: string;
  meterStart: string;
  meterStop: string;
  transactionId: number;
  transactionStartsAt: string;
  transactionEndsAt: string;
  transactionEndReason: string;
  walletHistoryId: string;
  isFreeTransaction: boolean;
  isTransactionIncompleteDueToOffline: boolean;
  rfidNo: string;

  sampledData: {
    measurand: string;
    context: string;
    value: string;
    unit: string;
  };

  walletHistory: {
    _id: string;
    type: string;
    amount: string;
    stripeCustomer: string;
    currency: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeCustomer: {
    _id: string;
    stripeCustomerId: string;
    paymentMethodId: string;
    cardHolderName: string;
    funding: string;
    brand: string;
    last4: number;
    expiryMonth: number;
    expiryYear: number;
    userCustomId: string;
    isGuestUser: boolean;
    isSelected: boolean;
    deletedAt: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripeAuthorizePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  stripePaymentInfo: {
    _id: string;
    paymentIntendId: string;
    stripeCustomerObjectId: string;
    amount: string;
    currency: string;
    status: string;
    userCustomId: string;
    isGuestUser: boolean;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };

  chargingSchedule: {
    _id: string;
    chargingPointSn: string;
    vehicleId: string;
    connectorId: number;
    startDateUtc: string;
    endDateUtc: string;
    userCustomId: string;
    accountCustomId: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
  };
}

/**
 * Station side panel
 * @param properties
 * @returns
 */
function TransactionSidePanel(properties: TransactionSidePanelProperties) {
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();

  const [transactionData, setTransactionData] = useState<TransactionDataProperties>();

  const transactionMap = [
    {
      header: 'Transaction Details',
      keyValuePair: [
        { label: 'Charging Point SN', key: 'chargingPointSn' },
        { label: 'Connector', key: 'connector' },
        { label: 'Status', key: 'status' },
        { label: 'Transaction Starts At', key: 'transactionStartsAt', date: true },
        { label: 'Transaction Ends At', key: 'transactionEndsAt', date: true },
        { label: 'Transaction End Reason', key: 'transactionEndReason' },
        { label: 'Initial Charge Percentage', key: 'initialChargePercentage' },
        { label: 'Desired Charge Percentage', key: 'desiredChargePercentage' },
        { label: 'Battery Capacity (kWh)', key: 'batteryCapacity' },
        { label: 'Meter Start', key: 'meterStart' },
        { label: 'Meter Stop', key: 'meterStop' },
        { label: 'Is Free Transaction', key: 'isFreeTransaction' },
        {
          label: 'Is Transaction Complete Due To Offline',
          key: 'isTransactionIncompleteDueToOffline',
        },
        {
          label: 'RFID',
          key: 'rfidNo',
        },
      ],
    },
    {
      header: 'Dynamic Pricing',
      keyValuePair: [
        { label: 'Pricing Type', key: 'dynamicPricing.pricingType' },
        { label: 'Price', key: 'dynamicPricing.price' },
      ],
    },
    {
      header: 'Revenue Details',
      keyValuePair: [
        { label: 'Revenue', key: 'revenue.foRevenue' },
        {
          label: 'Electricity Cost By Utility Provider',
          key: 'revenue.electricityCostByUtilityProvider',
        },
      ],
    },
    {
      header: 'Sampled Data',
      keyValuePair: [
        { label: 'Measurand', key: 'sampledData.measurand' },
        { label: 'Context', key: 'sampledData.context' },
        { label: 'Value', key: 'sampledData.value' },
        { label: 'Unit', key: 'sampledData.unit' },
      ],
    },
    {
      header: 'Wallet History',
      keyValuePair: [
        { label: 'Wallet History ID', key: 'walletHistory._id' },
        { label: 'Type', key: 'walletHistory.type' },
        { label: 'Amount', key: 'walletHistory.amount' },
        { label: 'Currency', key: 'walletHistory.currency' },
        { label: 'Stripe Customer Ref', key: 'walletHistory.stripeCustomer' },
        { label: 'User Custom ID', key: 'walletHistory.userCustomId' },
        { label: 'Is Guest User', key: 'walletHistory.isGuestUser' },
        { label: 'Created At', key: 'walletHistory.createdAt', date: true },
      ],
    },
    // {
    //   header: 'Stripe Customer',
    //   keyValuePair: [
    //     { label: 'Stripe Customer Object ID', key: 'stripeCustomer._id' },
    //     { label: 'Stripe Customer ID', key: 'stripeCustomer.stripeCustomerId' },
    //     { label: 'Payment Method ID', key: 'stripeCustomer.paymentMethodId' },
    //     { label: 'Card Holder Name', key: 'stripeCustomer.cardHolderName' },
    //     { label: 'Funding', key: 'stripeCustomer.funding' },
    //     { label: 'Card Brand', key: 'stripeCustomer.brand' },
    //     { label: 'Card Last 4 Digits', key: 'stripeCustomer.last4' },
    //     { label: 'Expiry Month', key: 'stripeCustomer.expiryMonth' },
    //     { label: 'Expiry Year', key: 'stripeCustomer.expiryYear' },
    //     { label: 'User Custom ID', key: 'stripeCustomer.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripeCustomer.isGuestUser' },
    //     { label: 'Is Selected', key: 'stripeCustomer.isSelected' },
    //     { label: 'Created At', key: 'stripeCustomer.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: 'Stripe Authorized Payment',
    //   keyValuePair: [
    //     { label: 'Authorized Payment ID', key: 'stripeAuthorizePaymentInfo._id' },
    //     { label: 'Payment Intent ID', key: 'stripeAuthorizePaymentInfo.paymentIntendId' },
    //     { label: 'Stripe Customer Ref', key: 'stripeAuthorizePaymentInfo.stripeCustomerObjectId' },
    //     { label: 'Amount', key: 'stripeAuthorizePaymentInfo.amount' },
    //     { label: 'Currency', key: 'stripeAuthorizePaymentInfo.currency' },
    //     { label: 'Status', key: 'stripeAuthorizePaymentInfo.status' },
    //     { label: 'User Custom ID', key: 'stripeAuthorizePaymentInfo.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripeAuthorizePaymentInfo.isGuestUser' },
    //     { label: 'Created At', key: 'stripeAuthorizePaymentInfo.createdAt', date: true },
    //   ],
    // },
    // {
    //   header: 'Stripe Final Payment',
    //   keyValuePair: [
    //     { label: 'Final Payment ID', key: 'stripePaymentInfo._id' },
    //     { label: 'Payment Intent ID', key: 'stripePaymentInfo.paymentIntendId' },
    //     { label: 'Stripe Customer Ref', key: 'stripePaymentInfo.stripeCustomerObjectId' },
    //     { label: 'Amount', key: 'stripePaymentInfo.amount' },
    //     { label: 'Currency', key: 'stripePaymentInfo.currency' },
    //     { label: 'Status', key: 'stripePaymentInfo.status' },
    //     { label: 'User Custom ID', key: 'stripePaymentInfo.userCustomId' },
    //     { label: 'Is Guest User', key: 'stripePaymentInfo.isGuestUser' },
    //     { label: 'Created At', key: 'stripePaymentInfo.createdAt', date: true },
    //   ],
    // },
    {
      header: 'Charging Schedule',
      keyValuePair: [
        { label: 'Schedule ID', key: 'chargingSchedule._id' },
        { label: 'Charging Point SN (Schedule)', key: 'chargingSchedule.chargingPointSn' },
        { label: 'Vehicle ID', key: 'chargingSchedule.vehicleId' },
        { label: 'Connector ID', key: 'chargingSchedule.connectorId' },
        { label: 'Start Date', key: 'chargingSchedule.startDateUtc', date: true },
        { label: 'End Date', key: 'chargingSchedule.endDateUtc', date: true },
        { label: 'User Custom ID', key: 'chargingSchedule.userCustomId' },
        { label: 'Account Custom ID', key: 'chargingSchedule.accountCustomId' },
        { label: 'Status', key: 'chargingSchedule.status' },
        { label: 'Created At', key: 'chargingSchedule.createdAt', date: true },
      ],
    },
    {
      header: 'User Info',
      keyValuePair: [
        { label: 'User Custom ID', key: 'userCustomId' },
        { label: 'Account Custom ID', key: 'accountCustomId' },
        { label: 'Is Guest User', key: 'isGuestUser' },
      ],
    },
    // {
    //   header: 'Meta Info',
    //   keyValuePair: [
    //     { label: 'Transaction Object ID', key: '_id' },
    //     { label: 'Wallet History ID', key: 'walletHistoryId' },
    //     { label: 'Stripe Customer Ref ID', key: 'stripeCustomerObjectId' },
    //     { label: 'Authorized Payment Ref ID', key: 'authorizePaymentIntentObjectId' },
    //     { label: 'Final Payment Ref ID', key: 'paymentIntentObjectId' },
    //     { label: 'Created At', key: 'createdAt', date: true },
    //   ],
    // },
  ];

  /**
   * Function to fetch station details
   * @param transactionId
   */
  const handleFetchStationDetails = async (transactionId: string) => {
    transaction
      .fetchTransactionById(transactionId)
      .then((response) => {
        setTransactionData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to get nested values from transactionData using dot notation
   * @param path dot-separated key path
   * @returns value or undefined
   */
  const getNestedValue = (path: string, utcToDate: boolean): any => {
    let value;
    try {
      value = path
        .split('.')
        .reduce((accumulator, key) => accumulator?.[key], transactionData as any);
      if (utcToDate) {
        console.log(value);
        value = `${ConvertUTCtoDate(value)} - ${FormatUtcString(ConvertUTCtoHHMM(value))}`;
      }
    } catch (error) {
      console.error(error);
    }
    return value;
  };

  useEffect(() => {
    let transactionObjectId;
    if (fetchIdFrom === 'queryParams') {
      transactionObjectId = searchParameters.get('transactionObjectId');
    } else if (fetchIdFrom === 'props') {
      transactionObjectId = id;
    }

    if (!transactionObjectId) {
      return;
    }

    handleFetchStationDetails(transactionObjectId);
  }, [searchParameters, id]);

  return (
    <div className="transaction-side-panel-wrap">
      <div className="transaction-side-panel-container">
        {transactionMap.map((section) => (
          <div className="transaction-section">
            <h1>{section.header}</h1>
            {section.keyValuePair.map((item) => {
              const value = getNestedValue(item.key, item.date || false);
              return (
                <div className="transaction-item">
                  <h2>{item.label}: </h2>
                  <p>{value !== undefined && value !== null ? String(value) : '—'}</p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TransactionSidePanel;
